import { useState } from "react";
import { AdminOpsShell } from "../dashboard/AdminOps";

export function AdminStorefrontNavigationPage({
  locale,
  texts,
  canEditStorefront,
  navMenuDraft,
  expandedNavRows,
  editingNavPath,
  setEditingNavPath,
  savingNavMenu,
  saveNavigationMenu,
  addNavMenuItem,
  expandAllNavRows,
  collapseAllNavRows,
  renderNavMenuRows
}) {
  const canEdit = canEditStorefront;

  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "店铺结构" : "Storefront Structure"}
      title={locale === "zh" ? "导航菜单管理" : "Navigation Management"}
      description={locale === "zh" ? "统一维护前台导航树，Products 分支会继续和数据库中的 product_groups 同步。" : "Manage the storefront navigation tree in the shared shell while keeping the Products branch synced with product_groups."}
      toolbar={(
        <div className="row-actions">
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={() => addNavMenuItem()}
            disabled={!canEdit}
          >
            {locale === "zh" ? "新增主菜单" : "Add Root Item"}
          </button>
          <button type="button" className="controlhub-button ghost" onClick={expandAllNavRows}>
            {locale === "zh" ? "展开全部" : "Expand All"}
          </button>
          <button type="button" className="controlhub-button ghost" onClick={collapseAllNavRows}>
            {locale === "zh" ? "收起全部" : "Collapse All"}
          </button>
          <button
            type="button"
            className="controlhub-button"
            onClick={async () => {
              const saved = await saveNavigationMenu();
              if (saved) {
                setEditingNavPath("");
              }
            }}
            disabled={!canEdit || savingNavMenu}
          >
            {savingNavMenu ? (locale === "zh" ? "保存中..." : "Saving...") : texts.saveNavigation}
          </button>
        </div>
      )}
      metrics={[
        { title: locale === "zh" ? "主菜单" : "Root Items", value: navMenuDraft.length, detail: locale === "zh" ? "当前顶层导航数量" : "Current top-level navigation rows", tone: "blue" },
        { title: locale === "zh" ? "展开节点" : "Expanded Nodes", value: expandedNavRows.size, detail: locale === "zh" ? "当前展开中的导航节点" : "Nodes currently expanded", tone: "violet" },
        { title: locale === "zh" ? "编辑权限" : "Edit Access", value: canEdit ? (locale === "zh" ? "可写" : "Writable") : (locale === "zh" ? "只读" : "Read-only"), detail: locale === "zh" ? "当前账号导航维护权限" : "Current account navigation access", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-surface">
        <p className="small muted">
          {locale === "zh"
            ? "Products 的子菜单与孙子菜单会按数据库表 product_groups 同步加载；在这里修改后会直接写回该表。"
            : "Products child menus stay synced with the product_groups table and writes here persist back to it."}
        </p>
        <p className="controlhub-nav-note">
          {editingNavPath
            ? (locale === "zh" ? "当前有菜单项处于编辑状态，点击保存后会统一写回导航配置。" : "A menu row is currently being edited. Save to persist the full navigation tree.")
            : (locale === "zh" ? "旧版导航编辑面板已恢复，可直接在右侧表格中继续维护菜单结构。" : "The legacy navigation editor is restored. Continue maintaining the menu structure directly in the table below.")}
        </p>
        <form
          className="controlhub-nav-settings-form"
          onSubmit={async (event) => {
            event.preventDefault();
            const saved = await saveNavigationMenu();
            if (saved) {
              setEditingNavPath("");
            }
          }}
        >
          <div className="table-wrap controlhub-nav-tree-wrap">
            <table className="controlhub-table controlhub-nav-tree-table">
              <thead>
                <tr>
                  <th>{locale === "zh" ? "菜单名称" : "Menu Name"}</th>
                  <th>{locale === "zh" ? "路径" : "Path"}</th>
                  <th>{locale === "zh" ? "图标" : "Icon"}</th>
                  <th>{locale === "zh" ? "状态" : "Status"}</th>
                  <th>{texts.actions}</th>
                </tr>
              </thead>
              <tbody>{renderNavMenuRows(navMenuDraft)}</tbody>
            </table>
          </div>
          <div className="row-actions controlhub-nav-toolbar">
            <button type="button" className="controlhub-button ghost" onClick={() => addNavMenuItem()} disabled={!canEdit}>
              {locale === "zh" ? "新增主菜单" : "Add Root Item"}
            </button>
            {editingNavPath ? (
              <button
                type="button"
                className="controlhub-button ghost"
                onClick={() => setEditingNavPath("")}
                disabled={savingNavMenu}
              >
                {locale === "zh" ? "退出编辑" : "Exit Editing"}
              </button>
            ) : null}
            <button type="submit" className="controlhub-button" disabled={!canEdit || savingNavMenu}>
              {savingNavMenu ? (locale === "zh" ? "保存中..." : "Saving...") : texts.saveNavigation}
            </button>
          </div>
        </form>
      </section>
    </AdminOpsShell>
  );
}

export function AdminStorefrontBasePage({
  locale,
  texts,
  storeSettingsDraft,
  setStoreSettingsDraft,
  canEditStorefront,
  savingStoreSettings,
  saveStoreBaseSettings,
  applyingStorefrontTheme,
  applyStorefrontThemeTemplate,
  resetStorefrontThemeTemplate
}) {
  const themeOptions = [
    {
      value: "style-1",
      label: locale === "zh" ? "风格 1 · 经典商务" : "Style 1 - Classic Commerce",
      note: locale === "zh" ? "稳重导航 + 标准栅格" : "Balanced nav and standard grid"
    },
    {
      value: "style-2",
      label: locale === "zh" ? "风格 2 · 自然柔和" : "Style 2 - Natural Soft",
      note: locale === "zh" ? "柔和配色 + 卡片化导购" : "Natural palette and card-led guidance"
    },
    {
      value: "style-3",
      label: locale === "zh" ? "风格 3 · 深色科技" : "Style 3 - Dark Tech",
      note: locale === "zh" ? "深色信息密度 + 强对比" : "Dark density and high contrast"
    }
  ];

  const activeThemeTemplate = String(storeSettingsDraft.themeTemplate || "style-1");

  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "店铺基础配置" : "Storefront Base Config"}
      title={texts.storeBase}
      description={locale === "zh" ? "统一维护站点标题、联系方式和核心 SEO 文案，不改动现有保存链路。" : "Maintain storefront title, contact channels, and core SEO copy in the shared shell without changing the existing save flow."}
      metrics={[
        { title: locale === "zh" ? "已配置联系项" : "Contact Fields", value: [storeSettingsDraft.contactEmail, storeSettingsDraft.contactPhone, storeSettingsDraft.contactWhatsapp].filter(Boolean).length, detail: locale === "zh" ? "邮箱、电话与 WhatsApp" : "Email, phone, and WhatsApp configured", tone: "blue" },
        { title: locale === "zh" ? "前台标题" : "Storefront Title", value: storeSettingsDraft.storefrontTitle || "-", detail: locale === "zh" ? "当前前台站点标题" : "Current storefront title", tone: "violet" },
        { title: locale === "zh" ? "写入权限" : "Write Access", value: canEditStorefront ? (locale === "zh" ? "可写" : "Writable") : (locale === "zh" ? "只读" : "Read-only"), detail: locale === "zh" ? "当前账号店铺编辑权限" : "Current storefront write capability", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-surface">
        <form className="controlhub-form controlhub-product-detail-form" onSubmit={saveStoreBaseSettings}>
          <section className="controlhub-theme-picker">
            <header className="controlhub-theme-picker-head">
              <h4>{locale === "zh" ? "模板预览与快速应用" : "Template Preview and Quick Apply"}</h4>
              <p>{locale === "zh" ? "可先一键应用模板，再按需调整标题、联系方式和 SEO。" : "Apply a template in one click, then fine-tune title, contacts, and SEO fields."}</p>
            </header>
            <div className="controlhub-theme-grid">
              {themeOptions.map((option) => (
                <article
                  key={option.value}
                  className={`controlhub-theme-card${activeThemeTemplate === option.value ? " is-active" : ""}`}
                >
                  <div className={`controlhub-theme-thumb ${option.value}`}>
                    <div className="controlhub-theme-thumb-bar" />
                    <div className="controlhub-theme-thumb-hero" />
                    <div className="controlhub-theme-thumb-grid">
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                  <p className="controlhub-theme-card-title">{option.label}</p>
                  <p className="controlhub-theme-card-note">{option.note}</p>
                  <div className="controlhub-theme-card-actions">
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => setStoreSettingsDraft((prev) => ({ ...prev, themeTemplate: option.value }))}
                      disabled={!canEditStorefront || applyingStorefrontTheme}
                    >
                      {locale === "zh" ? "选择" : "Select"}
                    </button>
                    <button
                      type="button"
                      className="controlhub-button"
                      onClick={() => {
                        void applyStorefrontThemeTemplate(option.value);
                      }}
                      disabled={!canEditStorefront || applyingStorefrontTheme || activeThemeTemplate === option.value}
                    >
                      {locale === "zh" ? "一键应用" : "Apply"}
                    </button>
                  </div>
                </article>
              ))}
            </div>
            <div className="controlhub-theme-picker-foot">
              <button
                type="button"
                className="controlhub-button ghost"
                onClick={() => {
                  void resetStorefrontThemeTemplate();
                }}
                disabled={!canEditStorefront || applyingStorefrontTheme || activeThemeTemplate === "style-1"}
              >
                {locale === "zh" ? "恢复默认模板" : "Restore Default Template"}
              </button>
            </div>
          </section>

          <div className="controlhub-form-grid two-col">
            <label>
              <span>{locale === "zh" ? "网站模板风格" : "Website Template Style"}</span>
              <select
                value={storeSettingsDraft.themeTemplate || "style-1"}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, themeTemplate: event.target.value }))
                }
                disabled={!canEditStorefront || applyingStorefrontTheme}
              >
                {themeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>{texts.storefrontTitle}</span>
              <input
                value={storeSettingsDraft.storefrontTitle}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, storefrontTitle: event.target.value }))
                }
                disabled={!canEditStorefront || applyingStorefrontTheme}
              />
            </label>
          </div>
          <div className="controlhub-form-grid two-col">
            <label>
              <span>{locale === "zh" ? "前台 Logo URL" : "Storefront Logo URL"}</span>
              <input
                value={storeSettingsDraft.logoUrl}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, logoUrl: event.target.value }))
                }
                disabled={!canEditStorefront}
                placeholder="/assets/about/logo.png"
              />
            </label>
            <label>
              <span>{locale === "zh" ? "Logo 替代文本" : "Logo Alt Text"}</span>
              <input
                value={storeSettingsDraft.logoAlt}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, logoAlt: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
          </div>
          <div className="controlhub-form-grid two-col">
            <label>
              <span>{locale === "zh" ? "顶部通知文案" : "Top Notice Text"}</span>
              <input
                value={storeSettingsDraft.topNoticeText}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, topNoticeText: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "联系邮箱" : "Contact Email"}</span>
              <input
                value={storeSettingsDraft.contactEmail}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, contactEmail: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
          </div>
          <div className="controlhub-form-grid two-col">
            <label>
              <span>{locale === "zh" ? "联系电话" : "Contact Phone"}</span>
              <input
                value={storeSettingsDraft.contactPhone}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, contactPhone: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "WhatsApp" : "WhatsApp"}</span>
              <input
                value={storeSettingsDraft.contactWhatsapp}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, contactWhatsapp: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
          </div>
          <div className="controlhub-form-grid two-col">
            <label>
              <span>{texts.adminTitle}</span>
              <input
                value={storeSettingsDraft.adminTitle}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, adminTitle: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{texts.seoMetaTitle}</span>
              <input
                value={storeSettingsDraft.seoMetaTitle}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, seoMetaTitle: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
          </div>
          <div className="controlhub-form-grid two-col">
            <label>
              <span>{texts.seoDescription}</span>
              <textarea
                rows={4}
                value={storeSettingsDraft.seoDescription}
                onChange={(event) =>
                  setStoreSettingsDraft((prev) => ({ ...prev, seoDescription: event.target.value }))
                }
                disabled={!canEditStorefront}
              />
            </label>
          </div>

          <div className="row-actions">
            <button
              type="submit"
              className="controlhub-button"
              disabled={savingStoreSettings || applyingStorefrontTheme || !canEditStorefront}
            >
              {texts.saveBaseSettings}
            </button>
          </div>
        </form>
      </section>
    </AdminOpsShell>
  );
}

export function AdminTemplateStudioPage({
  locale,
  texts,
  storeSettingsDraft,
  setStoreSettingsDraft,
  canEditStorefront,
  savingStoreSettings,
  saveTemplateStudioSettings,
  saveAsCustomThemeTemplate,
  applyCustomThemeTemplate,
  clearActiveCustomThemeTemplate
}) {
  const [templateName, setTemplateName] = useState("");
  const homeModuleOptions = [
    { key: "intro", labelZh: "首屏简介", labelEn: "Intro" },
    { key: "quick-nav", labelZh: "快速导航", labelEn: "Quick Nav" },
    { key: "categories", labelZh: "品类入口", labelEn: "Categories" },
    { key: "best-selling", labelZh: "热销商品", labelEn: "Best Selling" },
    { key: "featured", labelZh: "精选推荐", labelEn: "Featured" },
    { key: "solutions", labelZh: "解决方案", labelEn: "Solutions" },
    { key: "scenarios", labelZh: "应用场景", labelEn: "Scenarios" },
    { key: "story", labelZh: "品牌故事", labelEn: "Story" },
    { key: "process", labelZh: "合作流程", labelEn: "Process" },
    { key: "inquiry", labelZh: "询盘引导", labelEn: "Inquiry" }
  ];
  const catalogSectionOptions = [
    { key: "filters", labelZh: "筛选侧栏", labelEn: "Filter Sidebar" },
    { key: "hero", labelZh: "目录页标题区", labelEn: "Catalog Header" },
    { key: "productGrid", labelZh: "商品网格", labelEn: "Product Grid" },
    { key: "pagination", labelZh: "分页", labelEn: "Pagination" }
  ];
  const detailSectionOptions = [
    { key: "media", labelZh: "媒体图集", labelEn: "Media Gallery" },
    { key: "variations", labelZh: "规格选择", labelEn: "Variations" },
    { key: "pricing", labelZh: "阶梯价格", labelEn: "Tier Pricing" },
    { key: "description", labelZh: "摘要描述", labelEn: "Summary Description" },
    { key: "specs", labelZh: "产品规格", labelEn: "Specifications" },
    { key: "actions", labelZh: "询盘按钮", labelEn: "Inquiry Actions" },
    { key: "trustNote", labelZh: "信任提示", labelEn: "Trust Note" },
    { key: "content", labelZh: "详情内容", labelEn: "Detail Content" }
  ];

  const themeLayoutConfig = storeSettingsDraft?.themeLayoutConfig || {};
  const themePageConfigs = storeSettingsDraft?.themePageConfigs || {};
  const moduleOrderFromDraft = Array.isArray(themeLayoutConfig?.home?.moduleOrder) ? themeLayoutConfig.home.moduleOrder : [];
  // 区块顺序与显隐
  const homeModuleVisibility = themePageConfigs?.home?.moduleVisibility || {};
  const moduleOrder = [
    ...moduleOrderFromDraft.filter((key) => homeModuleOptions.some((item) => item.key === key)),
    ...homeModuleOptions.map((item) => item.key).filter((key) => !moduleOrderFromDraft.includes(key))
  ];

  function setHomeModuleVisibility(moduleKey, visible) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themePageConfigs: {
        ...prev.themePageConfigs,
        home: {
          ...prev.themePageConfigs?.home,
          moduleVisibility: {
            ...prev.themePageConfigs?.home?.moduleVisibility,
            [moduleKey]: !!visible
          }
        }
      }
    }));
  }

  function updateThemeLayoutConfig(nextLayoutConfig) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themeLayoutConfig: {
        ...prev.themeLayoutConfig,
        ...nextLayoutConfig
      }
    }));
  }

  function updatePageField(pageKey, field, value) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themePageConfigs: {
        ...prev.themePageConfigs,
        [pageKey]: {
          ...prev.themePageConfigs?.[pageKey],
          [field]: value
        }
      }
    }));
  }

  function updatePageSectionVisibility(pageKey, sectionKey, visible) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themePageConfigs: {
        ...prev.themePageConfigs,
        [pageKey]: {
          ...prev.themePageConfigs?.[pageKey],
          sectionVisibility: {
            ...prev.themePageConfigs?.[pageKey]?.sectionVisibility,
            [sectionKey]: !!visible
          }
        }
      }
    }));
  }

  function updateHeaderField(field, value) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themeLayoutConfig: {
        ...prev.themeLayoutConfig,
        header: {
          ...prev.themeLayoutConfig?.header,
          [field]: value
        }
      }
    }));
  }

  function updateCatalogField(field, value) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themeLayoutConfig: {
        ...prev.themeLayoutConfig,
        catalog: {
          ...prev.themeLayoutConfig?.catalog,
          [field]: value
        }
      }
    }));
  }

  function updateDetailField(field, value) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themeLayoutConfig: {
        ...prev.themeLayoutConfig,
        detail: {
          ...prev.themeLayoutConfig?.detail,
          [field]: value
        }
      }
    }));
  }

  function moveHomeModule(moduleKey, direction) {
    const index = moduleOrder.indexOf(moduleKey);
    if (index === -1) {
      return;
    }

    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= moduleOrder.length) {
      return;
    }

    const nextOrder = [...moduleOrder];
    const [picked] = nextOrder.splice(index, 1);
    nextOrder.splice(targetIndex, 0, picked);

    updateThemeLayoutConfig({
      home: {
        ...(themeLayoutConfig.home || {}),
        moduleOrder: nextOrder
      }
    });
  }

  const [selectedHomeModule, setSelectedHomeModule] = useState(null);

  function updateHomeModuleField(moduleKey, field, value) {
    setStoreSettingsDraft((prev) => ({
      ...prev,
      themePageConfigs: {
        ...prev.themePageConfigs,
        home: {
          ...prev.themePageConfigs?.home,
          modules: {
            ...prev.themePageConfigs?.home?.modules,
            [moduleKey]: {
              ...prev.themePageConfigs?.home?.modules?.[moduleKey],
              [field]: value
            }
          }
        }
      }
    }));
  }

  const customThemeTemplates = Array.isArray(storeSettingsDraft?.customThemeTemplates)
    ? storeSettingsDraft.customThemeTemplates
    : [];

  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "模板工作台" : "Template Studio"}
      title={locale === "zh" ? "可视化模板编辑" : "Visual Template Editor"}
      description={locale === "zh"
        ? "在此直接调整头部样式、首页模块顺序、目录页与详情页布局，并可保存为自定义模板。"
        : "Edit header style, homepage module order, catalog layout, and detail layout, then save as reusable custom templates."}
      metrics={[
        {
          title: locale === "zh" ? "当前主题" : "Active Theme",
          value: storeSettingsDraft.themeTemplate || "style-1",
          detail: locale === "zh" ? "基础主题模板" : "Base theme template",
          tone: "blue"
        },
        {
          title: locale === "zh" ? "自定义模板" : "Custom Templates",
          value: customThemeTemplates.length,
          detail: locale === "zh" ? "已保存模板数量" : "Saved template count",
          tone: "violet"
        },
        {
          title: locale === "zh" ? "编辑权限" : "Edit Access",
          value: canEditStorefront ? (locale === "zh" ? "可写" : "Writable") : (locale === "zh" ? "只读" : "Read-only"),
          detail: locale === "zh" ? "当前账号模板编辑权限" : "Current template editing capability",
          tone: "emerald"
        }
      ]}
    >
      <section className="admin-ops-surface">
        <div className="controlhub-form-grid two-col">
          <label>
            <span>{locale === "zh" ? "头部高度" : "Header Height"}</span>
            <input
              type="range"
              min={60}
              max={110}
              step={1}
              value={Number(themeLayoutConfig?.header?.height || 78)}
              onChange={(event) => updateHeaderField("height", Number(event.target.value || 78))}
              disabled={!canEditStorefront}
            />
          </label>
          <label>
            <span>{locale === "zh" ? "导航对齐" : "Navigation Alignment"}</span>
            <select
              value={String(themeLayoutConfig?.header?.navAlign || "center")}
              onChange={(event) => updateHeaderField("navAlign", event.target.value)}
              disabled={!canEditStorefront}
            >
              <option value="left">{locale === "zh" ? "左对齐" : "Left"}</option>
              <option value="center">{locale === "zh" ? "居中" : "Center"}</option>
              <option value="space-between">{locale === "zh" ? "两端对齐" : "Space Between"}</option>
            </select>
          </label>
          <label>
            <span>{locale === "zh" ? "导航按钮形态" : "Navigation Button Style"}</span>
            <select
              value={String(themeLayoutConfig?.header?.navButtonStyle || "pill")}
              onChange={(event) => updateHeaderField("navButtonStyle", event.target.value)}
              disabled={!canEditStorefront}
            >
              <option value="pill">{locale === "zh" ? "胶囊" : "Pill"}</option>
              <option value="square">{locale === "zh" ? "方角" : "Square"}</option>
              <option value="underline">{locale === "zh" ? "下划线" : "Underline"}</option>
            </select>
          </label>
          <label>
            <span>{locale === "zh" ? "目录页每行卡片数" : "Catalog Cards Per Row"}</span>
            <select
              value={Number(themeLayoutConfig?.catalog?.cardColumns || 3)}
              onChange={(event) => updateCatalogField("cardColumns", Number(event.target.value || 3))}
              disabled={!canEditStorefront}
            >
              <option value={2}>2</option>
              <option value={3}>3</option>
              <option value={4}>4</option>
            </select>
          </label>
          <label>
            <span>{locale === "zh" ? "目录页筛选区位置" : "Catalog Filter Position"}</span>
            <select
              value={String(themeLayoutConfig?.catalog?.sidebarMode || "left")}
              onChange={(event) => updateCatalogField("sidebarMode", event.target.value)}
              disabled={!canEditStorefront}
            >
              <option value="left">{locale === "zh" ? "左侧" : "Left"}</option>
              <option value="top">{locale === "zh" ? "顶部" : "Top"}</option>
            </select>
          </label>
          <label>
            <span>{locale === "zh" ? "详情页布局" : "Product Detail Layout"}</span>
            <select
              value={String(themeLayoutConfig?.detail?.layoutMode || "split")}
              onChange={(event) => updateDetailField("layoutMode", event.target.value)}
              disabled={!canEditStorefront}
            >
              <option value="split">{locale === "zh" ? "左右分栏" : "Split"}</option>
              <option value="stacked">{locale === "zh" ? "上下堆叠" : "Stacked"}</option>
              <option value="media-right">{locale === "zh" ? "媒体右侧" : "Media Right"}</option>
            </select>
          </label>
        </div>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "首页区块顺序与显隐" : "Homepage Module Order & Visibility"}</h4>
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {moduleOrder.map((moduleKey, index) => {
              const option = homeModuleOptions.find((item) => item.key === moduleKey);
              const visible = homeModuleVisibility[moduleKey] !== false;
              return (
                <div key={moduleKey} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", opacity: visible ? 1 : 0.5, background: visible ? "#fff" : "#f8fafc" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={visible}
                      onChange={e => setHomeModuleVisibility(moduleKey, e.target.checked)}
                      disabled={!canEditStorefront}
                      style={{ marginRight: 8 }}
                    />
                    <strong>{locale === "zh" ? option?.labelZh : option?.labelEn}</strong>
                  </div>
                  <div className="row-actions" style={{ margin: 0 }}>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => moveHomeModule(moduleKey, "up")}
                      disabled={!canEditStorefront || index === 0}
                    >
                      {locale === "zh" ? "上移" : "Up"}
                    </button>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => moveHomeModule(moduleKey, "down")}
                      disabled={!canEditStorefront || index === moduleOrder.length - 1}
                    >
                      {locale === "zh" ? "下移" : "Down"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "首页区块编辑" : "Home Module Editor"}</h4>
          <div className="controlhub-form-grid two-col" style={{ marginTop: 12 }}>
            <label>
              <span>{locale === "zh" ? "选择区块" : "Select Module"}</span>
              <select
                value={selectedHomeModule || ""}
                onChange={(e) => setSelectedHomeModule(e.target.value || null)}
                disabled={!canEditStorefront}
              >
                <option value="">{locale === "zh" ? "未选择" : "None"}</option>
                {moduleOrder.map((m) => {
                  const opt = homeModuleOptions.find(i => i.key === m);
                  return <option key={m} value={m}>{locale === "zh" ? opt?.labelZh : opt?.labelEn}</option>;
                })}
              </select>
            </label>
            <div style={{ gridColumn: '1 / -1' }}>
              {selectedHomeModule ? (() => {
                const moduleData = themePageConfigs?.home?.modules?.[selectedHomeModule] || {};
                return (
                  <div style={{ display: 'grid', gap: 8 }}>
                    <label>
                      <span>{locale === 'zh' ? '标题' : 'Title'}</span>
                      <input value={String(moduleData.title || '')} onChange={(e) => updateHomeModuleField(selectedHomeModule, 'title', e.target.value)} disabled={!canEditStorefront} />
                    </label>
                    <label>
                      <span>{locale === 'zh' ? '副标题' : 'Subtitle'}</span>
                      <input value={String(moduleData.subtitle || '')} onChange={(e) => updateHomeModuleField(selectedHomeModule, 'subtitle', e.target.value)} disabled={!canEditStorefront} />
                    </label>
                    <label>
                      <span>{locale === 'zh' ? '主按钮文案' : 'Primary CTA Text'}</span>
                      <input value={String(moduleData.primaryCtaText || '')} onChange={(e) => updateHomeModuleField(selectedHomeModule, 'primaryCtaText', e.target.value)} disabled={!canEditStorefront} />
                    </label>
                    <label>
                      <span>{locale === 'zh' ? '次按钮文案' : 'Secondary CTA Text'}</span>
                      <input value={String(moduleData.secondaryCtaText || '')} onChange={(e) => updateHomeModuleField(selectedHomeModule, 'secondaryCtaText', e.target.value)} disabled={!canEditStorefront} />
                    </label>
                    <label>
                      <span>{locale === 'zh' ? '图片 / 素材 URL' : 'Image / Asset URL'}</span>
                      <input value={String(moduleData.imageUrl || '')} onChange={(e) => updateHomeModuleField(selectedHomeModule, 'imageUrl', e.target.value)} disabled={!canEditStorefront} placeholder="/assets/about/1.png" />
                    </label>
                  </div>
                );
              })() : <em style={{ color: '#6b7280' }}>{locale === 'zh' ? '请选择一个区块进行编辑' : 'Select a module to edit'}</em>}
            </div>
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "自定义模板" : "Custom Templates"}</h4>
          <div className="controlhub-form-grid two-col" style={{ marginTop: 12 }}>
            <label>
              <span>{locale === "zh" ? "店铺 ID" : "Store ID"}</span>
              <input
                value={String(storeSettingsDraft.storeId || "default")}
                onChange={(event) => {
                  const nextValue = String(event.target.value || "")
                    .trim()
                    .toLowerCase()
                    .replace(/[^a-z0-9-_]+/g, "-")
                    .replace(/^-+|-+$/g, "");
                  setStoreSettingsDraft((prev) => ({
                    ...prev,
                    storeId: nextValue || "default"
                  }));
                }}
                placeholder={locale === "zh" ? "例如：store-001" : "e.g. store-001"}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "模板名称" : "Template Name"}</span>
              <input
                value={templateName}
                onChange={(event) => setTemplateName(event.target.value)}
                placeholder={locale === "zh" ? "例如：夏季活动模板" : "e.g. Summer Campaign"}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "已保存模板" : "Saved Templates"}</span>
              <select
                value={String(storeSettingsDraft.activeCustomThemeTemplateId || "")}
                onChange={(event) => {
                  const nextId = String(event.target.value || "");
                  if (!nextId) {
                    void clearActiveCustomThemeTemplate();
                    return;
                  }
                  void applyCustomThemeTemplate(nextId);
                }}
                disabled={!canEditStorefront || !customThemeTemplates.length}
              >
                <option value="">{locale === "zh" ? "未启用自定义模板" : "No custom template"}</option>
                {customThemeTemplates.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "页面可视化文案" : "Page Visual Content"}</h4>
          <div className="controlhub-form-grid two-col" style={{ marginTop: 12 }}>
            <label>
              <span>{locale === "zh" ? "首页眉题" : "Home Eyebrow"}</span>
              <input
                value={String(themePageConfigs?.home?.heroEyebrow || "")}
                onChange={(event) => updatePageField("home", "heroEyebrow", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "首页主标题" : "Home Title"}</span>
              <input
                value={String(themePageConfigs?.home?.heroTitle || "")}
                onChange={(event) => updatePageField("home", "heroTitle", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              <span>{locale === "zh" ? "首页副标题" : "Home Subtitle"}</span>
              <textarea
                rows={3}
                value={String(themePageConfigs?.home?.heroSubtitle || "")}
                onChange={(event) => updatePageField("home", "heroSubtitle", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "首页主按钮" : "Home Primary CTA"}</span>
              <input
                value={String(themePageConfigs?.home?.primaryCtaText || "")}
                onChange={(event) => updatePageField("home", "primaryCtaText", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "首页次按钮" : "Home Secondary CTA"}</span>
              <input
                value={String(themePageConfigs?.home?.secondaryCtaText || "")}
                onChange={(event) => updatePageField("home", "secondaryCtaText", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "目录页标题" : "Catalog Title"}</span>
              <input
                value={String(themePageConfigs?.catalog?.heroTitle || "")}
                onChange={(event) => updatePageField("catalog", "heroTitle", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "目录页按钮" : "Catalog CTA"}</span>
              <input
                value={String(themePageConfigs?.catalog?.ctaText || "")}
                onChange={(event) => updatePageField("catalog", "ctaText", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              <span>{locale === "zh" ? "目录页说明" : "Catalog Description"}</span>
              <textarea
                rows={3}
                value={String(themePageConfigs?.catalog?.heroDescription || "")}
                onChange={(event) => updatePageField("catalog", "heroDescription", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              <span>{locale === "zh" ? "目录页空状态文案" : "Catalog Empty Message"}</span>
              <input
                value={String(themePageConfigs?.catalog?.emptyMessage || "")}
                onChange={(event) => updatePageField("catalog", "emptyMessage", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "详情页询盘标题" : "Detail Inquiry Title"}</span>
              <input
                value={String(themePageConfigs?.detail?.inquiryTitle || "")}
                onChange={(event) => updatePageField("detail", "inquiryTitle", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "详情页信任提示" : "Detail Trust Note"}</span>
              <input
                value={String(themePageConfigs?.detail?.trustNote || "")}
                onChange={(event) => updatePageField("detail", "trustNote", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              <span>{locale === "zh" ? "详情页询盘说明" : "Detail Inquiry Description"}</span>
              <textarea
                rows={3}
                value={String(themePageConfigs?.detail?.inquiryDescription || "")}
                onChange={(event) => updatePageField("detail", "inquiryDescription", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "详情页主按钮" : "Detail Primary CTA"}</span>
              <input
                value={String(themePageConfigs?.detail?.primaryCtaText || "")}
                onChange={(event) => updatePageField("detail", "primaryCtaText", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
            <label>
              <span>{locale === "zh" ? "详情页次按钮" : "Detail Secondary CTA"}</span>
              <input
                value={String(themePageConfigs?.detail?.secondaryCtaText || "")}
                onChange={(event) => updatePageField("detail", "secondaryCtaText", event.target.value)}
                disabled={!canEditStorefront}
              />
            </label>
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "目录页区块显隐" : "Catalog Section Visibility"}</h4>
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {catalogSectionOptions.map((section) => {
              const visible = themePageConfigs?.catalog?.sectionVisibility?.[section.key] !== false;
              return (
                <label key={section.key} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: visible ? "#fff" : "#f8fafc", opacity: visible ? 1 : 0.55 }}>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(event) => updatePageSectionVisibility("catalog", section.key, event.target.checked)}
                    disabled={!canEditStorefront}
                  />
                  <strong>{locale === "zh" ? section.labelZh : section.labelEn}</strong>
                </label>
              );
            })}
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "详情页区块显隐" : "Detail Section Visibility"}</h4>
          <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
            {detailSectionOptions.map((section) => {
              const visible = themePageConfigs?.detail?.sectionVisibility?.[section.key] !== false;
              return (
                <label key={section.key} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", background: visible ? "#fff" : "#f8fafc", opacity: visible ? 1 : 0.55 }}>
                  <input
                    type="checkbox"
                    checked={visible}
                    onChange={(event) => updatePageSectionVisibility("detail", section.key, event.target.checked)}
                    disabled={!canEditStorefront}
                  />
                  <strong>{locale === "zh" ? section.labelZh : section.labelEn}</strong>
                </label>
              );
            })}
          </div>
        </article>

        <article className="controlhub-card" style={{ marginTop: 16 }}>
          <h4>{locale === "zh" ? "实时预览" : "Live Preview"}</h4>
          <div className="controlhub-form-grid three-col" style={{ marginTop: 12 }}>
            <section style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: "#fff" }}>
              <p className="controlhub-merchandising-caption">{locale === "zh" ? "首页" : "Home"}</p>
              <strong style={{ display: "block", marginTop: 8 }}>{String(themePageConfigs?.home?.heroEyebrow || "")}</strong>
              <h5 style={{ margin: "10px 0 8px" }}>{String(themePageConfigs?.home?.heroTitle || "")}</h5>
              <p style={{ margin: 0, color: "#475569" }}>{String(themePageConfigs?.home?.heroSubtitle || "")}</p>
              <div className="row-actions" style={{ marginTop: 12 }}>
                <button type="button" className="controlhub-button" disabled>{String(themePageConfigs?.home?.primaryCtaText || "")}</button>
                <button type="button" className="controlhub-button ghost" disabled>{String(themePageConfigs?.home?.secondaryCtaText || "")}</button>
              </div>
            </section>

            <section style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: "#fff" }}>
              <p className="controlhub-merchandising-caption">{locale === "zh" ? "商品目录" : "Catalog"}</p>
              <h5 style={{ margin: "10px 0 8px" }}>{String(themePageConfigs?.catalog?.heroTitle || "")}</h5>
              <p style={{ margin: 0, color: "#475569" }}>{String(themePageConfigs?.catalog?.heroDescription || "")}</p>
              <p style={{ margin: "12px 0 0", color: "#94a3b8" }}>{String(themePageConfigs?.catalog?.emptyMessage || "")}</p>
              <div className="row-actions" style={{ marginTop: 12 }}>
                <button type="button" className="controlhub-button" disabled>{String(themePageConfigs?.catalog?.ctaText || "")}</button>
              </div>
            </section>

            <section style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 16, background: "#fff" }}>
              <p className="controlhub-merchandising-caption">{locale === "zh" ? "商品详情" : "Detail"}</p>
              <h5 style={{ margin: "10px 0 8px" }}>{String(themePageConfigs?.detail?.inquiryTitle || "")}</h5>
              <p style={{ margin: 0, color: "#475569" }}>{String(themePageConfigs?.detail?.inquiryDescription || "")}</p>
              <p style={{ margin: "12px 0 0", color: "#0f766e" }}>{String(themePageConfigs?.detail?.trustNote || "")}</p>
              <div className="row-actions" style={{ marginTop: 12 }}>
                <button type="button" className="controlhub-button" disabled>{String(themePageConfigs?.detail?.primaryCtaText || "")}</button>
                <button type="button" className="controlhub-button ghost" disabled>{String(themePageConfigs?.detail?.secondaryCtaText || "")}</button>
              </div>
            </section>
          </div>
        </article>

        <div className="row-actions" style={{ marginTop: 16 }}>
          <button
            type="button"
            className="controlhub-button"
            onClick={() => {
              void saveTemplateStudioSettings();
            }}
            disabled={savingStoreSettings || !canEditStorefront}
          >
            {locale === "zh" ? "保存当前布局" : "Save Current Layout"}
          </button>
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={() => {
              void saveAsCustomThemeTemplate(templateName);
              setTemplateName("");
            }}
            disabled={savingStoreSettings || !canEditStorefront}
          >
            {locale === "zh" ? "另存为自定义模板" : "Save As Custom Template"}
          </button>
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={() => {
              void clearActiveCustomThemeTemplate();
            }}
            disabled={savingStoreSettings || !canEditStorefront || !storeSettingsDraft.activeCustomThemeTemplateId}
          >
            {locale === "zh" ? "停用自定义模板" : "Disable Custom Template"}
          </button>
        </div>
      </section>
    </AdminOpsShell>
  );
}

export function AdminArticleManagementPage({
  locale,
  texts,
  blogArticleDrafts,
  maxBlogArticleItems,
  canManageTrustContent,
  savingStoreSettings,
  addBlogArticleDraft,
  saveArticleManagementSettings,
  openBlogArticleEditor,
  removeBlogArticleDraft
}) {
  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "内容运营" : "Content Operations"}
      title={locale === "zh" ? "文章管理" : "Article Management"}
      description={locale === "zh" ? "以统一后台壳层管理博客内容，列表维护与弹窗编辑逻辑保持不变。" : "Manage blog content in the shared admin shell while keeping the existing list-and-modal editing flow."}
      toolbar={(
        <div className="row-actions">
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={addBlogArticleDraft}
            disabled={!canManageTrustContent || blogArticleDrafts.length >= maxBlogArticleItems}
          >
            {locale === "zh" ? "新增文章" : "Add Article"}
          </button>
          <button
            type="button"
            className="controlhub-button"
            onClick={() => {
              void saveArticleManagementSettings();
            }}
            disabled={savingStoreSettings || !canManageTrustContent}
          >
            {locale === "zh" ? "保存文章" : "Save Articles"}
          </button>
        </div>
      )}
      metrics={[
        { title: locale === "zh" ? "文章数" : "Articles", value: blogArticleDrafts.length, detail: locale === "zh" ? "当前已配置博客文章" : "Configured blog article rows", tone: "blue" },
        { title: locale === "zh" ? "容量" : "Capacity", value: `${blogArticleDrafts.length}/${maxBlogArticleItems}`, detail: locale === "zh" ? "当前可编辑容量" : "Current editable capacity", tone: "violet" },
        { title: locale === "zh" ? "编辑权限" : "Edit Access", value: canManageTrustContent ? (locale === "zh" ? "可写" : "Writable") : (locale === "zh" ? "只读" : "Read-only"), detail: locale === "zh" ? "当前内容维护权限" : "Current content management access", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-surface">
        <div className="controlhub-content-grid controlhub-products-fullscreen-grid">
          <article className="controlhub-card controlhub-full-width controlhub-blog-editor-shell">
            <div className="controlhub-faq-knowledge-head">
              <div>
                <h4>{locale === "zh" ? "文章管理" : "Article Management"}</h4>
                <p className="controlhub-merchandising-caption">
                  {locale === "zh"
                    ? "以列表方式管理 Blog 文章，点击编辑后在弹窗中修改文章详情。"
                    : "Manage blog articles in list view and edit details through a modal dialog."}
                </p>
              </div>
              <div className="controlhub-faq-knowledge-metrics">
                <span>{locale === "zh" ? "已配置" : "Configured"}</span>
                <strong>{`${blogArticleDrafts.length}/${maxBlogArticleItems}`}</strong>
              </div>
            </div>

            <div className="controlhub-blog-list" role="list">
              <div className="controlhub-blog-list-head" aria-hidden="true">
                <span>ID</span>
                <span>{locale === "zh" ? "文章内容" : "Content"}</span>
                <span>{locale === "zh" ? "分类" : "Category"}</span>
                <span>{locale === "zh" ? "文章内容简要" : "Summary"}</span>
                <span>{locale === "zh" ? "操作" : "Actions"}</span>
              </div>

              {blogArticleDrafts.map((item, index) => (
                <article key={item.id || index} className="controlhub-blog-list-row" role="listitem">
                  <div className="controlhub-blog-list-main">
                    <div className="controlhub-blog-list-id">
                      <strong>{item.id || `blog-${index + 1}`}</strong>
                      <span className="controlhub-blog-list-index">
                        {locale === "zh" ? `文章 ${index + 1}` : `Article ${index + 1}`}
                      </span>
                    </div>

                    <div className="controlhub-blog-list-content-preview">
                      <strong>{item.title || (locale === "zh" ? "未命名文章" : "Untitled article")}</strong>
                      <p>{String(item.body || "").trim() || (locale === "zh" ? "暂无正文" : "No article body yet")}</p>
                    </div>

                    <div className="controlhub-blog-list-category-preview">
                      {item.category || "-"}
                    </div>

                    <div className="controlhub-blog-list-summary-preview">
                      {item.summary || (locale === "zh" ? "暂无摘要" : "No summary")}
                    </div>

                    <div className="controlhub-blog-list-actions">
                      <button
                        type="button"
                        className="controlhub-button"
                        onClick={() => openBlogArticleEditor(index)}
                        disabled={!canManageTrustContent}
                      >
                        {locale === "zh" ? "编辑" : "Edit"}
                      </button>
                      <button
                        type="button"
                        className="controlhub-button ghost"
                        onClick={() => removeBlogArticleDraft(index)}
                        disabled={!canManageTrustContent || blogArticleDrafts.length <= 1}
                      >
                        {texts.remove}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </article>
        </div>
      </section>
    </AdminOpsShell>
  );
}

export function AdminCarouselPage({ locale, texts, canEditStorefront, savingBanners, renderBannerListView, saveCarouselBanners, addBannerDraft }) {
  return (
    <AdminOpsShell
      heroClassName="admin-ops-hero-compact"
      eyebrow={locale === "zh" ? "横幅编排" : "Banner Composition"}
      title={texts.designBanner || texts.designCarousel}
      description={locale === "zh" ? "管理横幅图片、文案、按钮与拖拽定位，新增与编辑都通过弹窗完成。" : "Manage banner images, copy, CTA, and drag positioning with modal-first add and edit flows."}
      toolbar={
        <button type="button" className="controlhub-button" onClick={addBannerDraft} disabled={!canEditStorefront || savingBanners}>
          {texts.addBanner}
        </button>
      }
    >
      <section className="admin-ops-surface">
        {renderBannerListView(saveCarouselBanners, savingBanners, texts.saveBanners, {
          showSaveButton: false,
          showAddButton: false
        })}
      </section>
    </AdminOpsShell>
  );
}

export function AdminCompanyImagesPage({
  locale,
  texts,
  companyImageSectionsDraft,
  maxCompanyImageItems,
  canEditStorefront,
  reportStorefrontWriteDenied,
  savingCompanyImages,
  saveCompanyImages,
  showCustomCompanySectionInput,
  setShowCustomCompanySectionInput,
  customCompanySectionName,
  setCustomCompanySectionName,
  addCustomCompanyImageSection,
  getCompanyImageSectionLocaleLabels,
  updateCompanyImageSectionEnabled,
  companyImageLinkDrafts,
  updateCompanyImageLinkDraft,
  handleAddCompanyImageLink,
  uploadingCompanyImageSectionKey,
  handleClearCompanyImageSection,
  normalizeCompanyImageValue,
  toCompanyImagePreviewUrl,
  companyImageDragging,
  handleCompanyImageDrop,
  openCompanyImagePreview,
  handleCompanyImageDragStart,
  handleCompanyImageDragEnd,
  moveCompanyImageUrl,
  handleRemoveCompanyImage,
  copyCompanyImageLink,
  uploadCompanyImages,
  companyIntroductionDraft,
  setCompanyIntroductionDraft
}) {
  const enabledSections = companyImageSectionsDraft.filter((section) => section.enabled !== false);
  const totalImageCount = companyImageSectionsDraft.reduce(
    (sum, section) => sum + (Array.isArray(section.images) ? section.images.length : 0),
    0
  );
  const disabledSectionCount = Math.max(0, companyImageSectionsDraft.length - enabledSections.length);

  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "企业形象资源" : "Company Media"}
      title={texts.companyImages}
      description={locale === "zh" ? "把图集、简介和拖拽排序统一放进新的内容工作台，保留现有上传与同步逻辑。" : "Bring company galleries, introduction content, and drag sorting into the refreshed content workspace without changing upload or sync logic."}
      metrics={[
        { title: locale === "zh" ? "启用图集" : "Enabled Sections", value: enabledSections.length, detail: locale === "zh" ? "当前启用的公司图集" : "Company image sections currently enabled", tone: "blue" },
        { title: locale === "zh" ? "图片总数" : "Images", value: totalImageCount, detail: locale === "zh" ? "当前图库条目数" : "Current gallery item count", tone: "violet" },
        { title: locale === "zh" ? "同步状态" : "Sync State", value: savingCompanyImages ? (locale === "zh" ? "同步中..." : "Syncing...") : (locale === "zh" ? "已就绪" : "Ready"), detail: locale === "zh" ? "公司图集保存状态" : "Company gallery save state", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-surface">
        <article className="controlhub-card controlhub-full-width controlhub-company-images-shell">
          <div className="controlhub-company-images-overview">
            <div>
              <h4>
                {texts.companyImages}
                <span>{` (${totalImageCount}/${maxCompanyImageItems})`}</span>
              </h4>
              <p className="controlhub-merchandising-caption">
                {locale === "zh"
                  ? "图集名称会在前台显示，建议按模块组织并保持每个模块主题清晰。"
                  : "Section names are exposed on the storefront, so keep each gallery grouped by a clear theme."}
              </p>
            </div>
            <div className="controlhub-company-images-head-actions">
              <span className="controlhub-company-images-helper-tag">
                {locale === "zh" ? "支持图片与文本混排" : "Images and text items supported"}
              </span>
              {!canEditStorefront ? (
                <button
                  type="button"
                  className="controlhub-button ghost"
                  onClick={reportStorefrontWriteDenied}
                >
                  {locale === "zh" ? "无编辑权限" : "No edit permission"}
                </button>
              ) : null}
              <button
                type="button"
                className="controlhub-button"
                onClick={saveCompanyImages}
                disabled={savingCompanyImages || !canEditStorefront}
              >
                {savingCompanyImages ? (locale === "zh" ? "保存中..." : "Saving...") : texts.saveCompanyImages}
              </button>
            </div>
          </div>

          <div className="controlhub-company-images-summary-grid">
            <article className="controlhub-company-images-summary-card">
              <span>{locale === "zh" ? "启用图集" : "Enabled Sections"}</span>
              <strong>{enabledSections.length}</strong>
              <small>{locale === "zh" ? "当前在前台展示的模块" : "Sections currently enabled on the storefront"}</small>
            </article>
            <article className="controlhub-company-images-summary-card">
              <span>{locale === "zh" ? "停用图集" : "Disabled Sections"}</span>
              <strong>{disabledSectionCount}</strong>
              <small>{locale === "zh" ? "可重新启用的备用模块" : "Spare modules that can be re-enabled"}</small>
            </article>
            <article className="controlhub-company-images-summary-card">
              <span>{locale === "zh" ? "素材占用" : "Asset Capacity"}</span>
              <strong>{`${totalImageCount}/${maxCompanyImageItems}`}</strong>
              <small>{locale === "zh" ? "总图库容量使用情况" : "Current usage against total gallery capacity"}</small>
            </article>
            <article className="controlhub-company-images-summary-card">
              <span>{locale === "zh" ? "简介状态" : "Introduction"}</span>
              <strong>{String(companyIntroductionDraft || "").trim() ? (locale === "zh" ? "已填写" : "Filled") : (locale === "zh" ? "待补充" : "Pending")}</strong>
              <small>{locale === "zh" ? "公司简介与图集一起构成企业形象页" : "Company introduction completes the gallery-led brand page"}</small>
            </article>
          </div>

          <section className="controlhub-company-images-control-panel">
            <div className="controlhub-company-images-control-head">
              <div>
                <h5>{locale === "zh" ? "图集开关与结构" : "Gallery Structure and Visibility"}</h5>
                <p className="controlhub-merchandising-caption">
                  {locale === "zh"
                    ? "先决定哪些模块对外展示，再补充每个模块的图片或文本项。"
                    : "Decide which sections should be published first, then add image or text items within each section."}
                </p>
              </div>
              <button
                type="button"
                className="controlhub-company-images-custom-trigger"
                onClick={() => setShowCustomCompanySectionInput((prev) => !prev)}
                disabled={!canEditStorefront}
              >
                +{locale === "zh" ? "自定义图集" : "Custom section"}
              </button>
            </div>

          <div className="controlhub-company-images-toggle-row">
            {companyImageSectionsDraft.map((section) => {
              const { labelZh: primaryZhLabel, labelEn: secondaryLabel } = getCompanyImageSectionLocaleLabels(section);
              return (
                <label key={`toggle-${section.key}`} className="controlhub-company-images-toggle-item">
                  <input
                    type="checkbox"
                    checked={section.enabled !== false}
                    onChange={(event) => updateCompanyImageSectionEnabled(section.key, event.target.checked)}
                    disabled={!canEditStorefront}
                  />
                  <span>
                    <strong>{primaryZhLabel || section.key}</strong>
                    {secondaryLabel ? <em>{secondaryLabel}</em> : null}
                  </span>
                </label>
              );
            })}
          </div>

          {showCustomCompanySectionInput ? (
            <div className="controlhub-company-images-custom-create-row">
              <input
                value={customCompanySectionName}
                placeholder={locale === "zh" ? "请输入自定义图集名称" : "Enter custom section name"}
                maxLength={40}
                onChange={(event) => setCustomCompanySectionName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addCustomCompanyImageSection();
                  }
                }}
                disabled={!canEditStorefront}
              />
              <span>{`${String(customCompanySectionName || "").trim().length}/40`}</span>
              <button
                type="button"
                className="controlhub-button"
                onClick={addCustomCompanyImageSection}
                disabled={!canEditStorefront || !String(customCompanySectionName || "").trim()}
              >
                {locale === "zh" ? "添加图集" : "Add section"}
              </button>
            </div>
          ) : null}
          </section>

          <div className="controlhub-company-images-content-grid">
            <div className="controlhub-company-images-board">
              <div className="controlhub-company-images-board-header">
                <div>
                  <h5>{locale === "zh" ? "图集内容工作区" : "Gallery Content Workspace"}</h5>
                  <p className="controlhub-company-images-board-tip">
                    {locale === "zh"
                      ? "支持模块内排序，也支持跨模块拖动图片，适合按企业故事线重新编排。"
                      : "You can sort within a section or drag across sections to reshape the storefront story flow."}
                  </p>
                </div>
                <span className="controlhub-company-images-helper-tag secondary">
                  {locale === "zh" ? `${enabledSections.length} 个模块启用中` : `${enabledSections.length} sections enabled`}
                </span>
              </div>

            {enabledSections.map((section) => {
              const { labelZh: sectionLabelZh, labelEn: sectionLabelEn } = getCompanyImageSectionLocaleLabels(section);
              return (
                <section key={section.key} className="controlhub-company-images-board-section">
                  <header className="controlhub-company-images-board-section-head">
                    <div>
                      <h5>
                        <span aria-hidden="true">*</span>
                        {sectionLabelZh || section.key}
                        {sectionLabelEn ? <small>{sectionLabelEn}</small> : null}
                      </h5>
                      <div className="controlhub-company-images-section-meta">
                        <span className="controlhub-company-images-section-stat">
                          {locale === "zh" ? `图片 ${Array.isArray(section.images) ? section.images.length : 0}` : `${Array.isArray(section.images) ? section.images.length : 0} items`}
                        </span>
                        {uploadingCompanyImageSectionKey === section.key ? (
                          <span className="controlhub-company-images-section-stat is-live">
                            {locale === "zh" ? "上传中" : "Uploading"}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="controlhub-company-images-board-links">
                      <button
                        type="button"
                        onClick={() => {
                          void handleClearCompanyImageSection(section.key);
                        }}
                        disabled={!canEditStorefront || !(section.images?.length > 0)}
                      >
                        {locale === "zh" ? "删除全部" : "Clear all"}
                      </button>
                    </div>
                  </header>

                  <div className="controlhub-company-images-url-row">
                    <input
                      type="url"
                      value={String(companyImageLinkDrafts?.[section.key] || "")}
                      placeholder={locale === "zh" ? "输入图片网址，例如 https://..." : "Enter an image URL, e.g. https://..."}
                      onChange={(event) => updateCompanyImageLinkDraft(section.key, event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          void handleAddCompanyImageLink(section.key);
                        }
                      }}
                      disabled={!canEditStorefront || savingCompanyImages || uploadingCompanyImageSectionKey === section.key}
                    />
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => {
                        void handleAddCompanyImageLink(section.key);
                      }}
                      disabled={!canEditStorefront || !String(companyImageLinkDrafts?.[section.key] || "").trim() || savingCompanyImages || uploadingCompanyImageSectionKey === section.key}
                    >
                      {locale === "zh" ? "添加网址图片" : "Add image URL"}
                    </button>
                  </div>

                  <div className="controlhub-company-images-grid">
                    {(Array.isArray(section.images) ? section.images : []).map((rawImageUrl, imageIndex) => {
                      const imageUrl = normalizeCompanyImageValue(rawImageUrl);
                      const previewUrl = toCompanyImagePreviewUrl(imageUrl);
                      const imageAlt = `${sectionLabelZh || section.key}-${imageIndex + 1}`;
                      const isDragging =
                        companyImageDragging?.sectionKey === section.key &&
                        companyImageDragging?.imageIndex === imageIndex;
                      return (
                        <article
                          key={`${section.key}-${imageIndex}`}
                          className={`controlhub-company-image-tile${isDragging ? " is-dragging" : ""}`}
                          onDragOver={(event) => {
                            if (!canEditStorefront || !companyImageDragging) {
                              return;
                            }
                            event.preventDefault();
                          }}
                          onDrop={(event) => {
                            event.preventDefault();
                            void handleCompanyImageDrop(section.key, imageIndex);
                          }}
                        >
                          <div className="controlhub-company-image-preview">
                            {previewUrl ? (
                              <button
                                type="button"
                                className="controlhub-company-image-preview-trigger"
                                onClick={() => openCompanyImagePreview(previewUrl, imageAlt)}
                                aria-label={locale === "zh" ? "查看大图" : "Open image preview"}
                                draggable={canEditStorefront}
                                onDragStart={() => handleCompanyImageDragStart(section.key, imageIndex)}
                                onDragEnd={handleCompanyImageDragEnd}
                              >
                                <img
                                  src={previewUrl}
                                  alt={imageAlt}
                                  loading="lazy"
                                />
                              </button>
                            ) : (
                              <span>{locale === "zh" ? "文本项" : "Text"}</span>
                            )}
                          </div>
                          <div className="controlhub-company-images-item-actions">
                            <button
                              type="button"
                              onClick={() => moveCompanyImageUrl(section.key, imageIndex, "up")}
                              disabled={!canEditStorefront || imageIndex === 0}
                            >
                              {locale === "zh" ? "上移" : "Up"}
                            </button>
                            <button
                              type="button"
                              onClick={() => moveCompanyImageUrl(section.key, imageIndex, "down")}
                              disabled={!canEditStorefront || imageIndex === (section.images?.length || 0) - 1}
                            >
                              {locale === "zh" ? "下移" : "Down"}
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                void handleRemoveCompanyImage(section.key, imageIndex);
                              }}
                              disabled={!canEditStorefront}
                            >
                              {texts.remove}
                            </button>
                            {previewUrl ? (
                              <a href={previewUrl} target="_blank" rel="noreferrer">
                                {locale === "zh" ? "预览" : "Preview"}
                              </a>
                            ) : null}
                            {previewUrl ? (
                              <button
                                type="button"
                                onClick={() => {
                                  void copyCompanyImageLink(previewUrl);
                                }}
                              >
                                {locale === "zh" ? "复制链接" : "Copy link"}
                              </button>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}

                    <label className="controlhub-company-image-upload-tile">
                      <span>{uploadingCompanyImageSectionKey === section.key
                        ? (locale === "zh" ? "上传中..." : "Uploading...")
                        : (locale === "zh" ? "上传图片" : "Upload")}</span>
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        className="controlhub-company-file-input"
                        onChange={(event) => {
                          void uploadCompanyImages(section.key, event);
                        }}
                        disabled={!canEditStorefront || uploadingCompanyImageSectionKey === section.key}
                      />
                    </label>
                  </div>
                </section>
              );
            })}

              {!enabledSections.length ? (
                <p className="controlhub-merchandising-caption">
                  {locale === "zh" ? "请先勾选要展示的图集" : "Select at least one section to manage images."}
                </p>
              ) : null}
            </div>

            <aside className="controlhub-company-introduction-panel">
              <h5>{texts.companyIntroductionTitle || (locale === "zh" ? "公司简介" : "Company Introduction")}</h5>
              <p className="controlhub-merchandising-caption">
                {texts.companyIntroductionHint || (locale === "zh"
                  ? "用于展示企业介绍、优势和服务能力。"
                  : "Describe your company profile, strengths, and service capabilities.")}
              </p>
              <div className="controlhub-company-introduction-tips">
                <span>{locale === "zh" ? "建议包含企业定位、工厂能力、交付优势。" : "Include positioning, factory capabilities, and delivery strengths."}</span>
                <strong>{`${String(companyIntroductionDraft || "").trim().length}/8000`}</strong>
              </div>
              <textarea
                rows={10}
                maxLength={8000}
                value={companyIntroductionDraft}
                onChange={(event) => setCompanyIntroductionDraft(event.target.value)}
                placeholder={texts.companyIntroductionPlaceholder || (locale === "zh" ? "请输入公司简介内容..." : "Enter company introduction content...")}
                disabled={!canEditStorefront}
              />
            </aside>
          </div>
        </article>
      </section>
    </AdminOpsShell>
  );
}

export function AdminFaqKnowledgePage({
  locale,
  texts,
  faqKnowledgeDrafts,
  maxFaqKnowledgeItems,
  adminRole,
  savingFaqKnowledge,
  addFaqDraft,
  saveFaqKnowledge,
  updateFaqDraftField,
  removeFaqDraft,
  reviewFilterProductId,
  setReviewFilterProductId,
  fetchAdminProductReviews,
  loadingProductReviews,
  reviewDraft,
  setReviewDraft,
  canManageTrustContent,
  savingProductReview,
  saveProductReviewDraft,
  productReviewRows,
  deleteProductReview
}) {
  return (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "知识与信任内容" : "Knowledge and Trust"}
      title={texts.faqKnowledge}
      description={locale === "zh" ? "把 FAQ 和买家评论维护统一放进内容工作台，保留原有保存和筛选逻辑。" : "Bring FAQ and buyer review maintenance into one content workspace while preserving the current save and filtering behavior."}
      toolbar={(
        <div className="row-actions">
          <button
            type="button"
            className="controlhub-button ghost"
            onClick={addFaqDraft}
            disabled={adminRole !== "admin" || faqKnowledgeDrafts.length >= maxFaqKnowledgeItems}
          >
            {texts.addFaq}
          </button>
          <button
            type="button"
            className="controlhub-button"
            onClick={saveFaqKnowledge}
            disabled={savingFaqKnowledge || adminRole !== "admin"}
          >
            {savingFaqKnowledge ? (locale === "zh" ? "保存中..." : "Saving...") : texts.saveFaqKnowledge}
          </button>
        </div>
      )}
      metrics={[
        { title: locale === "zh" ? "FAQ 数" : "FAQ Rows", value: faqKnowledgeDrafts.length, detail: locale === "zh" ? "当前知识库问答条目" : "Current knowledge-base FAQ rows", tone: "blue" },
        { title: locale === "zh" ? "评论数" : "Reviews", value: productReviewRows.length, detail: locale === "zh" ? "当前筛选结果下的评论数" : "Review rows under current filter", tone: "violet" },
        { title: locale === "zh" ? "内容权限" : "Content Access", value: canManageTrustContent ? (locale === "zh" ? "可写" : "Writable") : (locale === "zh" ? "只读" : "Read-only"), detail: locale === "zh" ? "当前 FAQ 与评论维护权限" : "Current FAQ and review management access", tone: "emerald" }
      ]}
    >
      <section className="admin-ops-surface">
        <article className="controlhub-card controlhub-full-width controlhub-faq-knowledge-shell">
          <div className="controlhub-faq-knowledge-head">
            <div>
              <h4>{texts.faqKnowledge}</h4>
              <p className="controlhub-merchandising-caption">
                {locale === "zh"
                  ? "将高频问答集中沉淀在知识库，前台与运营团队统一使用。"
                  : "Keep high-frequency responses in one consistent FAQ knowledge base for the storefront and operations."}
              </p>
            </div>
            <div className="controlhub-faq-knowledge-metrics">
              <span>{locale === "zh" ? "已配置" : "Configured"}</span>
              <strong>{`${faqKnowledgeDrafts.length}/${maxFaqKnowledgeItems}`}</strong>
            </div>
          </div>

          <div className="controlhub-faq-knowledge-grid">
            {faqKnowledgeDrafts.map((item, index) => (
              <article key={item.id || index} className="controlhub-faq-knowledge-item">
                <div className="controlhub-faq-knowledge-row-head">
                  <strong>{`Q${index + 1}`}</strong>
                  <span>{`${String(item.question || "").length}/${200}`}</span>
                </div>
                <textarea
                  rows={2}
                  value={item.question}
                  onChange={(event) => updateFaqDraftField(index, "question", event.target.value)}
                  placeholder={locale === "zh" ? "请输入问题" : "Enter question"}
                  disabled={adminRole !== "admin"}
                />

                <div className="controlhub-faq-knowledge-row-head">
                  <strong>{`A${index + 1}`}</strong>
                  <span>{`${String(item.answer || "").length}/${2000}`}</span>
                </div>
                <textarea
                  rows={5}
                  value={item.answer}
                  onChange={(event) => updateFaqDraftField(index, "answer", event.target.value)}
                  placeholder={locale === "zh" ? "请输入答案" : "Enter answer"}
                  disabled={adminRole !== "admin"}
                />

                <div className="controlhub-faq-knowledge-item-actions">
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => removeFaqDraft(index)}
                    disabled={adminRole !== "admin" || faqKnowledgeDrafts.length <= 1}
                  >
                    {texts.remove}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <section className="controlhub-product-review-admin">
            <div className="controlhub-faq-knowledge-head">
              <div>
                <h4>{locale === "zh" ? "买家评论管理" : "Buyer Review Management"}</h4>
                <p className="controlhub-merchandising-caption">
                  {locale === "zh"
                    ? "运营可手工添加评论用于增强店铺信任，也可按产品ID筛选和删除。"
                    : "Operations can manually add trust-building reviews, filter by product ID, and remove entries."}
                </p>
              </div>
            </div>

            <div className="controlhub-product-review-filter-row">
              <input
                type="number"
                min="1"
                step="1"
                value={reviewFilterProductId}
                onChange={(event) => setReviewFilterProductId(event.target.value)}
                placeholder={locale === "zh" ? "按产品ID筛选（可选）" : "Filter by product ID (optional)"}
              />
              <button
                type="button"
                className="controlhub-button ghost"
                onClick={() => fetchAdminProductReviews(reviewFilterProductId)}
                disabled={loadingProductReviews}
              >
                {loadingProductReviews ? (locale === "zh" ? "加载中..." : "Loading...") : texts.refresh}
              </button>
            </div>

            <div className="controlhub-product-review-form-grid">
              <input
                type="number"
                min="1"
                step="1"
                value={reviewDraft.productId}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, productId: event.target.value }))}
                placeholder={locale === "zh" ? "产品ID" : "Product ID"}
                disabled={!canManageTrustContent}
              />
              <input
                type="text"
                value={reviewDraft.reviewerName}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, reviewerName: event.target.value }))}
                placeholder={locale === "zh" ? "买家名称" : "Reviewer name"}
                disabled={!canManageTrustContent}
              />
              <input
                type="text"
                value={reviewDraft.country}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, country: event.target.value }))}
                placeholder={locale === "zh" ? "国家/地区" : "Country"}
                disabled={!canManageTrustContent}
              />
              <input
                type="number"
                min="1"
                max="5"
                step="1"
                value={reviewDraft.rating}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, rating: event.target.value }))}
                placeholder={locale === "zh" ? "评分(1-5)" : "Rating (1-5)"}
                disabled={!canManageTrustContent}
              />
              <input
                type="text"
                value={reviewDraft.variationText}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, variationText: event.target.value }))}
                placeholder={locale === "zh" ? "规格/变体说明" : "Variation info"}
                disabled={!canManageTrustContent}
              />
              <input
                type="date"
                value={reviewDraft.reviewDate}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, reviewDate: event.target.value }))}
                disabled={!canManageTrustContent}
              />
              <input
                type="number"
                min="0"
                step="1"
                value={reviewDraft.helpfulCount}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, helpfulCount: event.target.value }))}
                placeholder={locale === "zh" ? "有帮助次数" : "Helpful count"}
                disabled={!canManageTrustContent}
              />
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(reviewDraft.verifiedPurchase)}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, verifiedPurchase: event.target.checked }))}
                  disabled={!canManageTrustContent}
                />
                {locale === "zh" ? "已验证购买" : "Verified purchase"}
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(reviewDraft.repeatBuyer)}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, repeatBuyer: event.target.checked }))}
                  disabled={!canManageTrustContent}
                />
                {locale === "zh" ? "复购买家" : "Repeat buyer"}
              </label>
            </div>

            <textarea
              rows={4}
              value={reviewDraft.reviewText}
              onChange={(event) => setReviewDraft((prev) => ({ ...prev, reviewText: event.target.value }))}
              placeholder={locale === "zh" ? "评论内容" : "Review content"}
              disabled={!canManageTrustContent}
            />

            <div className="controlhub-faq-knowledge-toolbar">
              <button
                type="button"
                className="controlhub-button"
                onClick={saveProductReviewDraft}
                disabled={!canManageTrustContent || savingProductReview}
              >
                {savingProductReview
                  ? (locale === "zh" ? "保存中..." : "Saving...")
                  : (locale === "zh" ? "新增评论" : "Add review")}
              </button>
            </div>

            <div className="controlhub-product-review-list">
              {productReviewRows.map((item) => (
                <article key={`admin-review-${item.id}`} className="controlhub-product-review-item">
                  <header>
                    <strong>{String(item.reviewerName || "-")}</strong>
                    <span>{`#${item.id} · ${String(item.reviewDate || "-")}`}</span>
                  </header>
                  <p>
                    {`PID ${Number(item.productId || 0)} · Rating ${Number(item.rating || 5)}/5`}
                    {item.country ? ` · ${String(item.country)}` : ""}
                    {item.variationText ? ` · ${String(item.variationText)}` : ""}
                  </p>
                  <p>{String(item.reviewText || "")}</p>
                  <div className="controlhub-product-review-actions">
                    <span>
                      {item.verifiedPurchase ? (locale === "zh" ? "已验证购买" : "Verified") : (locale === "zh" ? "未验证" : "Unverified")}
                      {item.repeatBuyer ? ` · ${locale === "zh" ? "复购" : "Repeat"}` : ""}
                    </span>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => deleteProductReview(item.id)}
                      disabled={!canManageTrustContent}
                    >
                      {texts.deleteAction || texts.remove}
                    </button>
                  </div>
                </article>
              ))}
              {!productReviewRows.length ? (
                <p className="controlhub-merchandising-caption">
                  {locale === "zh" ? "暂无评论数据。" : "No review data yet."}
                </p>
              ) : null}
            </div>
          </section>
        </article>
      </section>
    </AdminOpsShell>
  );
}